'use client';

import { useId, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import Popover from './Popover';
import { Button } from './Button';

export type TimeRange = { start?: string; end?: string };

const toMinutes = (value?: string) => {
  if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return undefined;
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};
const formatTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export default function TimeRangePicker({
  value,
  onApply,
  initialOpen = false,
  onClose,
}: {
  value: TimeRange;
  onApply: (value: { start: string; end: string }) => void;
  initialOpen?: boolean;
  onClose?: () => void;
}) {
  const t = useTranslations('common');
  const id = useId();
  const [open, setOpen] = useState(initialOpen);
  const [draft, setDraft] = useState(value);
  const start = toMinutes(draft.start);
  const end = toMinutes(draft.end);
  const valid = start !== undefined && end !== undefined && start < end;
  const changeOpen = (next: boolean) => {
    if (next) setDraft(value);
    setOpen(next);
    if (!next) onClose?.();
  };

  return (
    <Popover
      popoverProps={{ open, onOpenChange: changeOpen }}
      contentClassName="w-72 p-3"
      trigger={
        <Button type="button" variant="ghost" className="h-auto px-0 py-0 font-normal gap-1">
          {value.start || '—'} – {value.end || '—'}
          <ChevronDown className="size-4" />
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {(['start', 'end'] as const).map(endpoint => {
          const current = toMinutes(draft[endpoint]);
          const min = endpoint === 'end' ? (start ?? 0) + 1 : 0;
          const max = endpoint === 'start' ? (end ?? 1439) - 1 : 1439;
          const hour = current === undefined ? undefined : Math.floor(current / 60);
          const selectClass =
            'h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
          return (
            <fieldset key={endpoint} className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-sm font-medium">{t(`timeRange.${endpoint}`)}</legend>
              <div className="flex items-center gap-2">
                <select
                  autoFocus={endpoint === 'start'}
                  aria-label={t('timeRange.hour')}
                  id={`${id}-${endpoint}-hour`}
                  className={selectClass}
                  value={hour ?? ''}
                  onChange={event => {
                    const candidate =
                      Number(event.target.value) * 60 + (current === undefined ? 0 : current % 60);
                    setDraft(previous => ({
                      ...previous,
                      [endpoint]: formatTime(Math.max(min, Math.min(max, candidate))),
                    }));
                  }}
                >
                  <option value="" disabled>
                    —
                  </option>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h} disabled={h * 60 > max || h * 60 + 59 < min}>
                      {String(h).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">:</span>
                <select
                  aria-label={t('timeRange.minute')}
                  className={selectClass}
                  disabled={hour === undefined}
                  value={current === undefined ? '' : current % 60}
                  onChange={event => {
                    if (hour === undefined) return;
                    const candidate = hour * 60 + Number(event.target.value);
                    if (candidate < min || candidate > max) return;
                    setDraft(previous => ({ ...previous, [endpoint]: formatTime(candidate) }));
                  }}
                >
                  <option value="" disabled>
                    —
                  </option>
                  {Array.from({ length: 60 }, (_, m) => (
                    <option
                      key={m}
                      value={m}
                      disabled={hour !== undefined && (hour * 60 + m < min || hour * 60 + m > max)}
                    >
                      {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>
          );
        })}
        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              const emptyRange = { start: '', end: '' };
              setDraft(emptyRange);
              if (value.start || value.end) onApply(emptyRange);
              changeOpen(false);
            }}
          >
            {t('clear')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!valid}
            onClick={() => {
              if (!valid) return;
              if (draft.start !== value.start || draft.end !== value.end) {
                onApply({ start: draft.start!, end: draft.end! });
              }
              changeOpen(false);
            }}
          >
            {t('apply')}
          </Button>
        </div>
      </div>
    </Popover>
  );
}
